import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 91 (studio.reports.section.soldExclusives.types.${exclusiveType}):
//   studio.reports.section.soldExclusives.types.fixed_price
//   studio.reports.section.soldExclusives.types.offers
//   studio.reports.section.soldExclusives.types.resales
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { sanitizeHref } from '@wix/da-url';
import ReportsSection from '../ReportsSection';
import ReportsTable from '../ReportsTable';
import ExclusiveTypeIcon from './ExclusiveTypeIcon/ExclusiveTypeIcon';
import { PapiStudioSellerStatsInit } from '@wix/da-papi-types';

import s from './SoldExclusivesSection.scss';

export interface Props {
  className?: string;
  data?: PapiStudioSellerStatsInit['soldExclusives'];
}

const SoldExclusivesSection: FC<Props> = ({ data, className }) => {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    return [
      {
        key: 'exclusiveType',
        label: t('studio.reports.section.soldExclusives.table.header.exclusiveType'),
      },
      {
        key: 'totalSales',
        label: t('studio.reports.section.soldExclusives.table.header.transactions'),
      },
      {
        key: 'uniqueCustomers',
        label: t('studio.reports.section.soldExclusives.table.header.uniqueOwners'),
      },
      {
        key: 'salesVolume',
        label: t('studio.reports.section.soldExclusives.table.header.sales'),
      },
    ];
  }, [t]);

  if (!data) {
    return null;
  }

  return (
    <ReportsSection
      className={className}
      reportType="soldExclusives"
      title={t('studio.reports.section.soldExclusives.title')}
      description={t('studio.reports.section.soldExclusives.description', {
        postProcess: ['reactPostprocessor'],
        learnMoreLink: (
          <a
            className="text-link-level-2-tertiary"
            href={sanitizeHref(
              t('studio.reports.section.soldExclusives.description.learnMoreLink.url')
            )}
          >
            {t('studio.reports.section.soldExclusives.description.learnMoreLink.text')}
          </a>
        ),
      })}
      data={data}
      dataFilter={dataToFilter =>
        /* do not shows rows that have no sales */
        dataToFilter?.filter(item => (item.totalSales || 0) > 0)
      }
      renderTable={(filteredData: any[]) => (
        <ReportsTable
          columns={columns}
          data={filteredData}
          renderRow={({ exclusiveType, totalSales, uniqueCustomers, salesVolume }: any) => (
            <>
              <td>
                <div className={s['icon-and-type']}>
                  <ExclusiveTypeIcon exclusiveType={exclusiveType} size={40} />
                  <div className={s['exclusive-type']}>
                    {t(`studio.reports.section.soldExclusives.types.${exclusiveType}`)}
                  </div>
                </div>
              </td>
              <td>{totalSales}</td>
              <td>{uniqueCustomers}</td>
              <td>{formatDollars(salesVolume)}</td>
            </>
          )}
          renderFooter={(totals: any) => {
            return (
              <tr>
                <td>{t('studio.reports.section.common.table.footer.total')}</td>
                <td>{totals?.totalSales}</td>
                <td>{totals?.uniqueCustomers}</td>
                <td>{formatDollars(totals?.salesVolume)}</td>
              </tr>
            );
          }}
        />
      )}
    />
  );
};

export default SoldExclusivesSection;
